import { memo } from 'react';
import { useGameDispatch } from '../../store/global-context';
import { useDragonHealthStore } from '../../store/zustand/useDragonHealthStore';
import { useGameStatusStore } from '../../store/zustand/useGameStatusStore';
import styles from './styles.module.scss';
import { Icon } from '../Icon';
import { roundTo } from '@game-libs/exc-libs/src/helpers';
import { CONFIG } from '../../config';
import { Progress } from 'radix-ui';
import { Debug, HotkeyHint } from '@game-libs/exc-libs';
import { ResourcesList } from '../../components/ResourcesList';
import { useTranslation } from 'react-i18next';
import { GUIActions } from '../../types/game-types';
import { useHotkeys } from 'react-hotkeys-hook';

export const HealthBar = memo(function HealthBar() {
  const dragonHealth = useDragonHealthStore((s) => s.dragonHealth);
  const dragonName = useGameStatusStore((s) => s.dragonName);
  const stunEffectTickDurationRemaining = useGameStatusStore((s) => s.stunEffectTickDurationRemaining);
  const dispatch = useGameDispatch();
  const { t } = useTranslation('game');

  useHotkeys(
    'G',
    () => {
      dispatch({ type: GUIActions.TOGGLE_UI });
    },
    [],
  );

  const maxHealth =
    CONFIG.game.initialDragonHealth[dragonName as keyof typeof CONFIG.game.initialDragonHealth];

  const showStunEffectMeter = stunEffectTickDurationRemaining > 0;
  const animateClosingStunEffectMeter = stunEffectTickDurationRemaining <= 1;

  return (
    <div className={styles.healthContainer}>
      <Progress.Root
        className={styles.healthBar}
        value={dragonHealth > maxHealth ? null : dragonHealth}
        max={maxHealth}
      >
        <Progress.Indicator
          className={styles.healthBarValue}
          style={{
            width: `${(dragonHealth / maxHealth) * 100}%`,
          }}
        />
      </Progress.Root>

      <div className={styles.dragonInfo}>
        <div className={styles.dragonName}>{t(dragonName)}</div>
        <div className={styles.dragonHealthValue} data-testid="dragon-health-value">
          {roundTo(dragonHealth, 0).toLocaleString('en')}
        </div>
      </div>

      {showStunEffectMeter && (
        <div className={`${styles.stunEffectMeter} ${animateClosingStunEffectMeter ? styles.animateClosing : ''}`}>
          <Icon iconId={'stun'} fileFormat={'gif'} large hasBackground={false} />
          <Progress.Root
            className={`${styles.stunBar} `}
            id="stun"
            value={stunEffectTickDurationRemaining}
            max={CONFIG.game.stunEffectTotalTickDuration}
          >
            <Progress.Indicator
              className={styles.stunBarValue}
              style={{
                width: `${(100 * stunEffectTickDurationRemaining) / CONFIG.game.stunEffectTotalTickDuration}%`,
              }}
            />
            <Debug>
              {stunEffectTickDurationRemaining / 1000}/{CONFIG.game.stunEffectTotalTickDuration / 1000}
            </Debug>
          </Progress.Root>
        </div>
      )}
      <div className={styles.resourcesContainer}>
        <ResourcesList />
      </div>
      <div
        className={styles.hideMenusButton}
        onClick={() => {
          dispatch({ type: GUIActions.TOGGLE_UI });
        }}
      >
        <Icon hasBackground={false} large iconId={'hide_menus_button'}>
          <HotkeyHint hint="G" top={12} />
        </Icon>
      </div>
    </div>
  );
});
